import { Icon } from '@/components/ui/icon'
import { describeGenerationFailure, generationPhaseLabel } from './generation-errors'
import type { GenerationFailure } from './generation-state'
import type { ShaderGenerationProgress } from '@/lib/canvas/shader/authoring/types'

interface ShaderGenerationStatusProps {
  running: boolean
  progress: ShaderGenerationProgress | null
  failure: GenerationFailure | null
  summary?: string
}

/** One line under the prompt box: progress while running, else the error with its fix, else the last summary. */
export function ShaderGenerationStatus({ running, progress, failure, summary }: ShaderGenerationStatusProps) {
  if (running) {
    const received = progress?.received
    return (
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground" role="status" aria-live="polite">
        <Icon name="loader-circle" className="h-3 w-3 animate-spin" />
        <span>{generationPhaseLabel(progress?.phase)}</span>
        {received ? <span className="tabular-nums">{received.toLocaleString()} chars</span> : null}
        {progress?.charges && progress.charges > 1 ? (
          <span className="text-amber-700 dark:text-amber-300" title="Fixing the shader is a second Genie credits request">
            second charge
          </span>
        ) : null}
      </div>
    )
  }

  if (failure) {
    const notice = describeGenerationFailure(failure.code, failure.message)
    return (
      <div className="rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-[10px] leading-snug text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200" role="alert">
        <div className="font-medium">{notice.title}</div>
        {notice.fix && <div className="mt-0.5 break-words text-red-700 dark:text-red-100/90">{notice.fix}</div>}
        {notice.detail && (
          <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-red-900 dark:text-red-100">{notice.detail}</pre>
        )}
      </div>
    )
  }

  if (summary) {
    return (
      <p className="line-clamp-2 text-[10px] leading-snug text-muted-foreground" title={summary}>
        {summary}
      </p>
    )
  }

  return null
}
